import { FormikValues } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';

import { Card } from '@material-ui/core';
import GavelIcon from '@material-ui/icons/Gavel';

import AppSnackbar from '../../../common/AppSnackbar';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineTextField from '../../../common/inline/InlineTextField';
import { useUpdateAgreement } from '../Agreement.hooks';
import { Agreement } from '../Agreement.interface';

export interface AgreementInfoCardProps {
  agreement: Agreement;
  onChange?: (agreement: Agreement) => void;
}

const getInitialValues = (agreement?: Agreement) => {
  return {
    name: agreement?.name || '',
    description: agreement?.description || ''
  };
};

const AgreementInfoCard: React.FC<AgreementInfoCardProps> = ({
  agreement,
  onChange
}) => {
  const onChangeRef = useRef(onChange); // prevent circular refresh
  const formikRef = useRef<FormikValues>();
  const {
    agreement: updatedAgreement,
    isLoading: isUpdating,
    error: updateError,
    patch
  } = useUpdateAgreement(agreement.id);

  const [initialValues, setInitialValues] = useState<Partial<Agreement>>();

  // set the initial state of the form once loaded
  useEffect(() => {
    setInitialValues(getInitialValues(agreement));
  }, [agreement]);
  // notify parent when a agreement record is updated
  useEffect(() => {
    if (updatedAgreement && onChangeRef && onChangeRef.current) {
      onChangeRef.current(updatedAgreement);
    }
  }, [updatedAgreement]);
  return (
    <Card>
      <CardTitle>
        <GavelIcon />
        Agreement Info
      </CardTitle>
      <CardContent padding='large'>
        <InlineFormik
          innerRef={(formik: any) => (formikRef.current = formik)}
          initialValues={initialValues}
          validationSchema={Yup.object({
            name: Yup.string().required('Required')
          })}
          isSubmitting={isUpdating}
          error={updateError}
          onSubmit={(agreement: Partial<Agreement>) => {
            patch(agreement);
          }}
        >
          <InlineForm>
            <InlineTextField
              name='name'
              label='Agreement Name'
              placeholder='Provide a name for the agreement.'
              disabled={agreement?.status === 'published'}
            />
            <InlineTextField
              multiline
              name='description'
              label='Description'
              placeholder='Provide an overview of the agreement and what will be covered under the agreement.'
              disabled={agreement?.status === 'published'}
            />
          </InlineForm>
        </InlineFormik>
        <AppSnackbar severity='error' open={Boolean(updateError)}>
          There was an error saving.
        </AppSnackbar>
      </CardContent>
    </Card>
  );
};

export default AgreementInfoCard;
